import { ConnectionStatus } from '#~/common/connection-status/index.js';
import { vendorName } from '@studio/design-system/themes/vendor.js';
import type { ReactElement } from 'react';
import { getImage } from './device-img.js';
import type { Agent } from '@studio/models/agent';
import { useAppVersion } from '#~/agent/hooks.js';
import { toVersion } from '@studio/models/agent';
import all_bridges_offline from '../../icon/all_bridges_offline.svg';
import { OperatingSystem, MachineType } from '@tryiris-ai/common.protojs/agent/v1/agent';

type SingleBridgeOfflineProps = {
  agentVersion: Agent['version'] | undefined;
  agentName: Agent['name'] | undefined;
};

/** Single bridge offline state component */
export const SingleBridgeOffline = ({
  agentVersion,
  agentName
}: SingleBridgeOfflineProps): ReactElement => {
  const appVersion = useAppVersion();

  // Calculate version after all hooks are called
  const version = !!agentVersion ? toVersion(agentVersion) : appVersion;

  // Use a static default image instead of GetAgentDeviceImage which might use hooks
  // This avoids potential hook ordering issues
  const defaultImage = getImage(OperatingSystem.OS_MAC, MachineType.MACHINE_TYPE_DESKTOP);

  return (
    <div className='flex max-w-prompt flex-col items-center gap-24 rounded-[32px] bg-base-100 p-32'>
      <div className='flex flex-col items-center gap-6'>
        <div className='text-lg font-demibold'>{agentName ?? '-'}</div>
        <div className='text-muted'>Agent version {version}</div>
      </div>
      <div className='relative'>
        <img src={defaultImage} className='w-[150px]' alt='Offline Bridge' />
        <div className='mt-12 flex justify-center'>
          <div className='bg-opacity-30 flex items-center gap-4 rounded-8 bg-base-300 px-12 py-6 text-sm font-medium'>
            <ConnectionStatus connected={false} className='text-[6px]' />
            Offline
          </div>
        </div>
      </div>

      <div className='max-w-[300px]'>
        <p className='text-center font-regular text-light'>
          {vendorName()} is unable to connect to your computer. Please restart the {vendorName()}{' '}
          application and refresh.
        </p>
      </div>
    </div>
  );
};

/** Multiple bridges offline state component */
export const AllBridgesOffline = (): ReactElement => (
  <div className='flex flex-col items-center gap-24 rounded-[32px] bg-base-100 p-48'>
    <div className='flex flex-col items-center gap-6'>
      <p className='text[#F7F7F7] mb-8 text-lg font-demibold'>All bridges are currently offline</p>
      <img
        src={all_bridges_offline}
        className='h-[102px] w-[112px] text-center'
        alt='Multple Offline Bridges'
      />
    </div>
    <div className='max-w-[296px]'>
      <p className='text-center font-regular text-light'>
        {vendorName()} is unable to connect to your computer. Please restart the {vendorName()}{' '}
        application and refresh.
      </p>
    </div>
  </div>
);
